import React, { useEffect, useState } from 'react';
import './LandingAdmin.css';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import defaultLandingConfig from '../../config/landing.config';

const randomId = (prefix = 'item') => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const emptyFeature = () => ({
  id: randomId('feature'),
  title: '',
  desc: '',
  mediaType: 'image',
  media: '',
});

const emptyTestimonial = () => ({
  id: randomId('testimonial'),
  name: '',
  text: '',
});

const emptyService = () => ({
  id: randomId('service'),
  title: '',
  desc: '',
  src: '',
  gallery: [],
});

const cloneCategoryStyles = (styles = []) =>
  styles.map((style) => ({
    id: style.id || randomId('style'),
    name: style.name || '',
    desc: style.desc || '',
    images: Array.isArray(style.images) ? [...style.images] : [],
  }));

const cloneProductCategories = (categories = []) =>
  categories.map((category) => ({
    id: category.id || randomId('category'),
    title: category.title || '',
    desc: category.desc || '',
    styles: cloneCategoryStyles(category.styles || []),
  }));

const cloneServices = (services = []) =>
  services.map((svc) => ({
    id: svc.id || randomId('service'),
    title: svc.title || '',
    desc: svc.desc || '',
    src: svc.src || '',
    gallery: Array.isArray(svc.gallery) ? [...svc.gallery] : [],
  }));

const formatWizardOptions = (options = []) =>
  (Array.isArray(options) ? options : [])
    .map((opt) => `${opt.id || ''}|${opt.label || ''}`)
    .filter((line) => line !== '|')
    .join('\n');

const parseWizardOptions = (value = '') =>
  value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [idPart, ...labelParts] = line.split('|');
      const id = (idPart || '').trim();
      const label = labelParts.length ? labelParts.join('|').trim() : id;
      const normalizedId = id || label;
      const normalizedLabel = label || id;
      return normalizedId || normalizedLabel
        ? { id: normalizedId, label: normalizedLabel }
        : null;
    })
    .filter(Boolean);

const oldPalette = new Set([
  '#f97316',
  '#ea580c',
  '#ffedd5',
  '#fff7ed',
  '#fcd34d',
  '#fb923c',
]);

const isLegacyContent = (text = '') =>
  typeof text === 'string' && /bbts|clover|tecnolog/i.test(text.toLowerCase());

const needsLegacyReset = (data = {}) =>
  isLegacyContent(data.siteTitle) ||
  isLegacyContent(data.siteName) ||
  isLegacyContent(data.hero?.title);

const needsThemeReset = (theme = {}) => {
  if (!theme) return false;
  return Object.values(theme).some(
    (value) => typeof value === 'string' && oldPalette.has(value.toLowerCase())
  );
};

const isVideoUrl = (url = '') => /\.(mp4|mov|m4v|webm|ogg)(\?.*)?$/i.test(url);

const normalizeLandingConfig = (data = {}) => {
  const heroBullets = Array.isArray(data.hero?.bullets)
    ? [...data.hero.bullets]
    : Array.isArray(defaultLandingConfig.hero?.bullets)
      ? [...defaultLandingConfig.hero.bullets]
      : [];
  const legacyReset = needsLegacyReset(data);
  const heroSource = typeof data.hero === 'object' && data.hero !== null ? data.hero : {};
  const hasHeroImage = Object.prototype.hasOwnProperty.call(heroSource, 'image');
  const heroImage = hasHeroImage ? heroSource.image : defaultLandingConfig.hero?.image || '';
  const mergedHero = legacyReset
    ? {
        ...defaultLandingConfig.hero,
        image: heroImage,
      }
    : {
        ...defaultLandingConfig.hero,
        ...heroSource,
        bullets: heroBullets,
        image: heroImage,
      };

  return {
    ...defaultLandingConfig,
    ...data,
    siteTitle: legacyReset
      ? defaultLandingConfig.siteTitle
      : data.siteTitle || defaultLandingConfig.siteTitle || defaultLandingConfig.siteName,
    siteName: legacyReset ? defaultLandingConfig.siteName : data.siteName || defaultLandingConfig.siteName,
    metaDescription:
      typeof data.metaDescription === 'string'
        ? data.metaDescription
        : defaultLandingConfig.metaDescription || '',
    metaKeywords:
      typeof data.metaKeywords === 'string'
        ? data.metaKeywords
        : defaultLandingConfig.metaKeywords || '',
    servicesTitle:
      typeof data.servicesTitle === 'string'
        ? data.servicesTitle
        : defaultLandingConfig.servicesTitle || '',
    hero: mergedHero,
    services: Array.isArray(data.services)
      ? cloneServices(data.services)
      : cloneServices(defaultLandingConfig.services || []),
    navigation: Array.isArray(data.navigation)
      ? [...data.navigation]
      : [...(defaultLandingConfig.navigation || [])],
    featuredProducts: Array.isArray(data.featuredProducts)
      ? [...data.featuredProducts]
      : [...(defaultLandingConfig.featuredProducts || [])],
    impactHighlights: Array.isArray(data.impactHighlights)
      ? [...data.impactHighlights]
      : [...(defaultLandingConfig.impactHighlights || [])],
    professionalSegments: Array.isArray(data.professionalSegments)
      ? [...data.professionalSegments]
      : [...(defaultLandingConfig.professionalSegments || [])],
    showProfessionalSection:
      typeof data.showProfessionalSection === 'boolean'
        ? data.showProfessionalSection
        : defaultLandingConfig.showProfessionalSection !== false,
    showTestimonialSection:
      typeof data.showTestimonialSection === 'boolean'
        ? data.showTestimonialSection
        : defaultLandingConfig.showTestimonialSection !== false,
    whyChoose: Array.isArray(data.whyChoose)
      ? [...data.whyChoose]
      : [...(defaultLandingConfig.whyChoose || [])],
    blogPosts: Array.isArray(data.blogPosts)
      ? [...data.blogPosts]
      : [...(defaultLandingConfig.blogPosts || [])],
    showWhySection:
      typeof data.showWhySection === 'boolean'
        ? data.showWhySection
        : defaultLandingConfig.showWhySection !== false,
    showBlogSection:
      typeof data.showBlogSection === 'boolean'
        ? data.showBlogSection
        : defaultLandingConfig.showBlogSection !== false,
    showContactSection:
      typeof data.showContactSection === 'boolean'
        ? data.showContactSection
        : defaultLandingConfig.showContactSection !== false,
    aboutSection: {
      ...(defaultLandingConfig.aboutSection || {}),
      ...(data.aboutSection || {}),
      images: Array.isArray(data.aboutSection?.images)
        ? [...data.aboutSection.images]
        : [...(defaultLandingConfig.aboutSection?.images || [])],
    },
    login: {
      ...defaultLandingConfig.login,
      ...(data.login || {}),
    },
    form: {
      ...defaultLandingConfig.form,
      ...(data.form || {}),
    },
    calendar: {
      ...defaultLandingConfig.calendar,
      ...(data.calendar || {}),
    },
    whatsappModal: {
      ...(defaultLandingConfig.whatsappModal || {}),
      ...(data.whatsappModal || {}),
    },
    theme: legacyReset || needsThemeReset(data.theme)
      ? { ...(defaultLandingConfig.theme || {}) }
      : {
          ...(defaultLandingConfig.theme || {}),
          ...(data.theme || {}),
        },
    contactInfo: {
      ...(defaultLandingConfig.contactInfo || {}),
      ...(data.contactInfo || {}),
      ctaCard: {
        ...(defaultLandingConfig.contactInfo?.ctaCard || {}),
        ...(data.contactInfo?.ctaCard || {}),
      },
    },
    footer: {
      ...(defaultLandingConfig.footer || {}),
      ...(data.footer || {}),
    },
    productCategories:
      Array.isArray(data.productCategories)
        ? cloneProductCategories(data.productCategories)
        : cloneProductCategories(defaultLandingConfig.productCategories || []),
    measurementGuidelines:
      Array.isArray(data.measurementGuidelines)
        ? [...data.measurementGuidelines]
        : [...(defaultLandingConfig.measurementGuidelines || [])],
    quoteSteps:
      Array.isArray(data.quoteSteps)
        ? [...data.quoteSteps]
        : [...(defaultLandingConfig.quoteSteps || [])],
    wizardOptions: {
      ...(defaultLandingConfig.wizardOptions || {}),
      ...(data.wizardOptions || {}),
      colors: Array.isArray(data.wizardOptions?.colors)
        ? data.wizardOptions.colors
        : defaultLandingConfig.wizardOptions?.colors || [],
      glass: Array.isArray(data.wizardOptions?.glass)
        ? data.wizardOptions.glass
        : defaultLandingConfig.wizardOptions?.glass || [],
      finish: Array.isArray(data.wizardOptions?.finish)
        ? data.wizardOptions.finish
        : defaultLandingConfig.wizardOptions?.finish || [],
      series: Array.isArray(data.wizardOptions?.series)
        ? data.wizardOptions.series
        : defaultLandingConfig.wizardOptions?.series || [],
    },
    copy: {
      ...(defaultLandingConfig.copy || {}),
      ...(data.copy || {}),
      header: {
        ...(defaultLandingConfig.copy?.header || {}),
        ...(data.copy?.header || {}),
        markets: Array.isArray(data.copy?.header?.markets)
          ? data.copy.header.markets
          : [...(defaultLandingConfig.copy?.header?.markets || [])],
      },
      hero: {
        ...(defaultLandingConfig.copy?.hero || {}),
        ...(data.copy?.hero || {}),
      },
      servicesSection: {
        ...(defaultLandingConfig.copy?.servicesSection || {}),
        ...(data.copy?.servicesSection || {}),
      },
      productSection: {
        ...(defaultLandingConfig.copy?.productSection || {}),
        ...(data.copy?.productSection || {}),
      },
      measurementSection: {
        ...(defaultLandingConfig.copy?.measurementSection || {}),
        ...(data.copy?.measurementSection || {}),
      },
      quoteStepsSection: {
        ...(defaultLandingConfig.copy?.quoteStepsSection || {}),
        ...(data.copy?.quoteStepsSection || {}),
        taglines: Array.isArray(data.copy?.quoteStepsSection?.taglines)
          ? data.copy.quoteStepsSection.taglines
          : [...(defaultLandingConfig.copy?.quoteStepsSection?.taglines || [])],
      },
      featuredProductsSection: {
        ...(defaultLandingConfig.copy?.featuredProductsSection || {}),
        ...(data.copy?.featuredProductsSection || {}),
      },
      impactSection: {
        ...(defaultLandingConfig.copy?.impactSection || {}),
        ...(data.copy?.impactSection || {}),
      },
      professionalSection: {
        ...(defaultLandingConfig.copy?.professionalSection || {}),
        ...(data.copy?.professionalSection || {}),
      },
      testimonialSection: {
        ...(defaultLandingConfig.copy?.testimonialSection || {}),
        ...(data.copy?.testimonialSection || {}),
      },
      whySection: {
        ...(defaultLandingConfig.copy?.whySection || {}),
        ...(data.copy?.whySection || {}),
      },
      blogSection: {
        ...(defaultLandingConfig.copy?.blogSection || {}),
        ...(data.copy?.blogSection || {}),
      },
      footerSection: {
        ...(defaultLandingConfig.copy?.footerSection || {}),
        ...(data.copy?.footerSection || {}),
      },
      contactSection: {
        ...(defaultLandingConfig.copy?.contactSection || {}),
        ...(data.copy?.contactSection || {}),
        labels: {
          ...(defaultLandingConfig.copy?.contactSection?.labels || {}),
          ...(data.copy?.contactSection?.labels || {}),
        },
      },
      wizard: {
        ...(defaultLandingConfig.copy?.wizard || {}),
        ...(data.copy?.wizard || {}),
        steps: Array.isArray(data.copy?.wizard?.steps)
          ? data.copy.wizard.steps
          : [...(defaultLandingConfig.copy?.wizard?.steps || [])],
      },
      modals: {
        ...(defaultLandingConfig.copy?.modals || {}),
        ...(data.copy?.modals || {}),
      },
      assistant: {
        ...(defaultLandingConfig.copy?.assistant || {}),
        ...(data.copy?.assistant || {}),
      },
    },
  };
};

const isPdfUrl = (url = '') => /\.pdf(\?.*)?$/i.test(url);

const LandingAdmin = () => {
  const [primaryForm, setPrimaryForm] = useState(() => normalizeLandingConfig(defaultLandingConfig));
  const [primaryLoading, setPrimaryLoading] = useState(true);
  const [primaryError, setPrimaryError] = useState(null);
  const [primarySaving, setPrimarySaving] = useState(false);
  const [primaryStatus, setPrimaryStatus] = useState('');
  const [activeTab, setActiveTab] = useState('identity');

  const [uploadingHint, setUploadingHint] = useState('');
  const [serviceGalleryInputs, setServiceGalleryInputs] = useState({});
  const [styleGalleryInputs, setStyleGalleryInputs] = useState({});
  const [aboutImageInput, setAboutImageInput] = useState('');

  const landingTabs = [
    { id: 'identity', label: 'Identidad' },
    { id: 'hero', label: 'Hero' },
    { id: 'theme', label: 'Tema' },
    { id: 'labels', label: 'Textos UI' },
    { id: 'services', label: 'Servicios' },
    { id: 'content', label: 'Contenido' },
    { id: 'about', label: 'Nosotros' },
    { id: 'catalog', label: 'Catálogo' },
    { id: 'guides', label: 'Guías' },
    { id: 'aux', label: 'Auxiliares' },
  ];

  useEffect(() => {
    let cancelled = false;
    const fetchPrimary = async () => {
      setPrimaryLoading(true);
      try {
        const docRef = doc(db, 'config', 'landing');
        const snap = await getDoc(docRef);
        if (!snap.exists()) {
          await setDoc(docRef, defaultLandingConfig);
          if (!cancelled) setPrimaryForm(normalizeLandingConfig(defaultLandingConfig));
          return;
        }
        const data = snap.data() || {};
        if (!cancelled) {
          setPrimaryForm(normalizeLandingConfig(data));
        }
      } catch (err) {
        console.error('❌ Error cargando landing principal:', err);
        if (!cancelled) {
          setPrimaryError(err);
          setPrimaryForm(normalizeLandingConfig(defaultLandingConfig));
        }
      } finally {
        if (!cancelled) setPrimaryLoading(false);
      }
    };

    fetchPrimary();
    return () => {
      cancelled = true;
    };
  }, []);

  const uploadAsset = async (file, pathPrefix = 'landing/uploads') => {
    const safePath = `${pathPrefix}/${Date.now()}-${file.name}`;
    const storageRef = ref(storage, safePath);
    const metadata = { contentType: file.type || 'application/octet-stream' };

    const uploadSimple = async () => {
      await uploadBytes(storageRef, file, metadata);
      return getDownloadURL(storageRef);
    };

    const uploadResumable = async () =>
      new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, file, metadata);
        uploadTask.on(
          'state_changed',
          null,
          (error) => reject(error),
          async () => {
            try {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(url);
            } catch (error) {
              reject(error);
            }
          }
        );
      });

    const shouldUseResumable = file.size > 5 * 1024 * 1024 || file.type.startsWith('video/');
    try {
      return shouldUseResumable ? await uploadResumable() : await uploadSimple();
    } catch (error) {
      console.error('❌ Error subiendo archivo (intentando fallback):', error?.serverResponse || error);
      return shouldUseResumable ? await uploadSimple() : await uploadResumable();
    }
  };

  const handlePrimaryField = (field, value) => {
    setPrimaryForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePrimaryHeroChange = (field, value) => {
    setPrimaryForm((prev) => ({
      ...prev,
      hero: {
        ...(prev.hero || {}),
        [field]: value,
      },
    }));
  };

  const handleHeroBulletsChange = (value) => {
    const bullets = value
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    setPrimaryForm((prev) => ({
      ...prev,
      hero: {
        ...(prev.hero || {}),
        bullets,
      },
    }));
  };

  const handlePrimaryWhatsappChange = (field, value) => {
    setPrimaryForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePrimaryModalChange = (field, value) => {
    setPrimaryForm((prev) => ({
      ...prev,
      whatsappModal: {
        ...(prev.whatsappModal || {}),
        [field]: value,
      },
    }));
  };

  const handlePrimaryLoginChange = (field, value) => {
    setPrimaryForm((prev) => ({
      ...prev,
      login: {
        ...(prev.login || {}),
        [field]: value,
      },
    }));
  };

  const handlePrimaryFormTextsChange = (field, value) => {
    setPrimaryForm((prev) => ({
      ...prev,
      form: {
        ...(prev.form || {}),
        [field]: value,
      },
    }));
  };

  const handlePrimaryCalendarChange = (field, value) => {
    setPrimaryForm((prev) => ({
      ...prev,
      calendar: {
        ...(prev.calendar || {}),
        [field]: value,
      },
    }));
  };

  const handleThemeChange = (field, value) => {
    setPrimaryForm((prev) => ({
      ...prev,
      theme: {
        ...(prev.theme || {}),
        [field]: value,
      },
    }));
  };

  const updateCopy = (section, field, value) => {
    setPrimaryForm((prev) => ({
      ...prev,
      copy: {
        ...(prev.copy || {}),
        [section]: {
          ...(prev.copy?.[section] || {}),
          [field]: value,
        },
      },
    }));
  };

  const updateCopyRoot = (field, value) => {
    setPrimaryForm((prev) => ({
      ...prev,
      copy: {
        ...(prev.copy || {}),
        [field]: value,
      },
    }));
  };

  const updateCopyNested = (section, nested, field, value) => {
    setPrimaryForm((prev) => ({
      ...prev,
      copy: {
        ...(prev.copy || {}),
        [section]: {
          ...(prev.copy?.[section] || {}),
          [nested]: {
            ...(prev.copy?.[section]?.[nested] || {}),
            [field]: value,
          },
        },
      },
    }));
  };

  const updateCopyArray = (section, field, value) => {
    const list = value
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    updateCopy(section, field, list);
  };

  const updateWizardOptions = (field, value) => {
    setPrimaryForm((prev) => ({
      ...prev,
      wizardOptions: {
        ...(prev.wizardOptions || {}),
        [field]: parseWizardOptions(value),
      },
    }));
  };

  const updatePrimaryService = (index, field, value) => {
    setPrimaryForm((prev) => {
      const services = [...(prev.services || [])];
      services[index] = {
        ...services[index],
        [field]: value,
      };
      return { ...prev, services };
    });
  };

  const addPrimaryService = () => {
    setPrimaryForm((prev) => ({
      ...prev,
      services: [...(prev.services || []), emptyService()],
    }));
  };

  const removePrimaryService = (index) => {
    setPrimaryForm((prev) => ({
      ...prev,
      services: (prev.services || []).filter((_, idx) => idx !== index),
    }));
  };

  const handlePrimaryLogoUpload = async (file) => {
    if (!file) return;
    setUploadingHint('Subiendo logo principal...');
    try {
      const url = await uploadAsset(file, 'landing/prwd/logo');
      handlePrimaryField('logo', url);
    } catch (err) {
      console.error('❌ Error subiendo logo', err);
      setPrimaryStatus('❌ No se pudo subir el logo.');
    } finally {
      setUploadingHint('');
    }
  };

  const handlePrimaryServiceImageUpload = async (index, file) => {
    if (!file) return;
    setUploadingHint('Subiendo imagen del servicio...');
    try {
      const url = await uploadAsset(file, `landing/prwd/services/${primaryForm.services?.[index]?.id || 'service'}`);
      updatePrimaryService(index, 'src', url);
    } catch (err) {
      console.error('❌ Error subiendo imagen del servicio', err);
      setPrimaryStatus('❌ No se pudo subir la imagen del servicio.');
    } finally {
      setUploadingHint('');
    }
  };

  const getServiceKey = (service, index) => service.id || `service-${index}`;

  const handlePrimaryServiceGalleryUpload = async (index, files) => {
    if (!files || !files.length) return;
    setUploadingHint('Subiendo galería del servicio...');
    try {
      const uploaded = [];
      for (const file of files) {
        const url = await uploadAsset(file, `landing/prwd/services/${primaryForm.services?.[index]?.id || 'service'}/gallery`);
        uploaded.push(url);
      }
      setPrimaryForm((prev) => {
        const services = [...(prev.services || [])];
        const gallery = services[index].gallery || [];
        services[index] = {
          ...services[index],
          gallery: [...gallery, ...uploaded],
        };
        return { ...prev, services };
      });
    } catch (err) {
      console.error('❌ Error subiendo galería del servicio', err);
      setPrimaryStatus('❌ No se pudo subir la galería.');
    } finally {
      setUploadingHint('');
    }
  };

  const handleServiceGalleryUrlChange = (serviceKey, value) => {
    setServiceGalleryInputs((prev) => ({
      ...prev,
      [serviceKey]: value,
    }));
  };

  const addServiceGalleryUrl = (index) => {
    const service = primaryForm.services?.[index];
    if (!service) return;
    const key = getServiceKey(service, index);
    const url = (serviceGalleryInputs[key] || '').trim();
    if (!url) return;

    setPrimaryForm((prev) => {
      const services = [...(prev.services || [])];
      const gallery = services[index].gallery || [];
      services[index] = {
        ...services[index],
        gallery: [...gallery, url],
      };
      return { ...prev, services };
    });

    setServiceGalleryInputs((prev) => ({
      ...prev,
      [key]: '',
    }));
  };

  const removePrimaryServiceGalleryItem = (serviceIndex, imgIndex) => {
    setPrimaryForm((prev) => {
      const services = [...(prev.services || [])];
      services[serviceIndex] = {
        ...services[serviceIndex],
        gallery: (services[serviceIndex].gallery || []).filter((_, idx) => idx !== imgIndex),
      };
      return { ...prev, services };
    });
  };

  const updateProductCategory = (index, field, value) => {
    setPrimaryForm((prev) => {
      const categories = [...(prev.productCategories || [])];
      categories[index] = {
        ...categories[index],
        [field]: value,
      };
      return { ...prev, productCategories: categories };
    });
  };

  const addProductCategory = () => {
    setPrimaryForm((prev) => ({
      ...prev,
      productCategories: [...(prev.productCategories || []), cloneProductCategories([{}])[0]],
    }));
  };

  const removeProductCategory = (index) => {
    setPrimaryForm((prev) => ({
      ...prev,
      productCategories: (prev.productCategories || []).filter((_, idx) => idx !== index),
    }));
  };

  const addCategoryStyle = (categoryIndex) => {
    setPrimaryForm((prev) => {
      const categories = [...(prev.productCategories || [])];
      const styles = categories[categoryIndex].styles || [];
      categories[categoryIndex] = {
        ...categories[categoryIndex],
        styles: [...styles, cloneCategoryStyles([{}])[0]],
      };
      return { ...prev, productCategories: categories };
    });
  };

  const updateCategoryStyle = (categoryIndex, styleIndex, field, value) => {
    setPrimaryForm((prev) => {
      const categories = [...(prev.productCategories || [])];
      const styles = [...(categories[categoryIndex].styles || [])];
      styles[styleIndex] = {
        ...styles[styleIndex],
        [field]: value,
      };
      categories[categoryIndex] = {
        ...categories[categoryIndex],
        styles,
      };
      return { ...prev, productCategories: categories };
    });
  };

  const removeCategoryStyle = (categoryIndex, styleIndex) => {
    setPrimaryForm((prev) => {
      const categories = [...(prev.productCategories || [])];
      const styles = (categories[categoryIndex].styles || []).filter((_, idx) => idx !== styleIndex);
      categories[categoryIndex] = { ...categories[categoryIndex], styles };
      return { ...prev, productCategories: categories };
    });
  };

  const getCategoryKey = (category, index) => category.id || `category-${index}`;

  const getStyleKey = (categoryIndex, styleIndex) => {
    const category = primaryForm.productCategories?.[categoryIndex];
    const style = category?.styles?.[styleIndex];
    const categoryId = getCategoryKey(category || {}, categoryIndex);
    const styleId = style?.id || `style-${styleIndex}`;
    return `${categoryId}-${styleId}`;
  };

  const handleStyleImageUpload = async (categoryIndex, styleIndex, file) => {
    if (!file) return;
    setUploadingHint('Subiendo imagen del estilo...');
    try {
      const category = primaryForm.productCategories?.[categoryIndex];
      const style = category?.styles?.[styleIndex];
      const categoryId = category?.id || `category-${categoryIndex}`;
      const styleId = style?.id || `style-${styleIndex}`;
      const url = await uploadAsset(
        file,
        `landing/prwd/productCategories/${categoryId}/${styleId}`
      );

      setPrimaryForm((prev) => {
        const categories = [...(prev.productCategories || [])];
        const styles = [...(categories[categoryIndex].styles || [])];
        const images = styles[styleIndex].images || [];
        styles[styleIndex] = {
          ...styles[styleIndex],
          images: [...images, url],
        };
        categories[categoryIndex] = {
          ...categories[categoryIndex],
          styles,
        };
        return { ...prev, productCategories: categories };
      });
      setPrimaryStatus('✅ Imagen añadida al estilo.');
    } catch (err) {
      console.error('❌ Error subiendo imagen de estilo', err);
      setPrimaryStatus('❌ No se pudo subir la imagen.');
    } finally {
      setUploadingHint('');
    }
  };

  const handleStyleImageUrlAdd = (categoryIndex, styleIndex) => {
    const key = getStyleKey(categoryIndex, styleIndex);
    const url = (styleGalleryInputs[key] || '').trim();
    if (!url) return;

    setPrimaryForm((prev) => {
      const categories = [...(prev.productCategories || [])];
      const styles = [...(categories[categoryIndex].styles || [])];
      const images = styles[styleIndex].images || [];
      styles[styleIndex] = {
        ...styles[styleIndex],
        images: [...images, url],
      };
      categories[categoryIndex] = {
        ...categories[categoryIndex],
        styles,
      };
      return { ...prev, productCategories: categories };
    });

    setStyleGalleryInputs((prev) => ({
      ...prev,
      [key]: '',
    }));
  };

  const removeStyleImage = (categoryIndex, styleIndex, imageIndex) => {
    setPrimaryForm((prev) => {
      const categories = [...(prev.productCategories || [])];
      const styles = [...(categories[categoryIndex].styles || [])];
      styles[styleIndex] = {
        ...styles[styleIndex],
        images: (styles[styleIndex].images || []).filter((_, idx) => idx !== imageIndex),
      };
      categories[categoryIndex] = {
        ...categories[categoryIndex],
        styles,
      };
      return { ...prev, productCategories: categories };
    });
  };

  const addMeasurementGuide = () => {
    setPrimaryForm((prev) => ({
      ...prev,
      measurementGuidelines: [...(prev.measurementGuidelines || []), { title: '', desc: '' }],
    }));
  };

  const updateMeasurementGuide = (index, field, value) => {
    setPrimaryForm((prev) => {
      const guidelines = [...(prev.measurementGuidelines || [])];
      guidelines[index] = { ...guidelines[index], [field]: value };
      return { ...prev, measurementGuidelines: guidelines };
    });
  };

  const removeMeasurementGuide = (index) => {
    setPrimaryForm((prev) => ({
      ...prev,
      measurementGuidelines: (prev.measurementGuidelines || []).filter((_, idx) => idx !== index),
    }));
  };

  const addQuoteStep = () => {
    setPrimaryForm((prev) => ({
      ...prev,
      quoteSteps: [...(prev.quoteSteps || []), { title: '', desc: '' }],
    }));
  };

  const updateQuoteStep = (index, field, value) => {
    setPrimaryForm((prev) => {
      const steps = [...(prev.quoteSteps || [])];
      steps[index] = { ...steps[index], [field]: value };
      return { ...prev, quoteSteps: steps };
    });
  };

  const removeQuoteStep = (index) => {
    setPrimaryForm((prev) => ({
      ...prev,
      quoteSteps: (prev.quoteSteps || []).filter((_, idx) => idx !== index),
    }));
  };

  const addNavigationItem = () => {
    setPrimaryForm((prev) => ({
      ...prev,
      navigation: [...(prev.navigation || []), { label: '', href: '#', sublabel: '' }],
    }));
  };

  const updateNavigationItem = (index, field, value) => {
    setPrimaryForm((prev) => {
      const navigation = [...(prev.navigation || [])];
      navigation[index] = { ...navigation[index], [field]: value };
      return { ...prev, navigation };
    });
  };

  const removeNavigationItem = (index) => {
    setPrimaryForm((prev) => ({
      ...prev,
      navigation: (prev.navigation || []).filter((_, idx) => idx !== index),
    }));
  };

  const addFeaturedProduct = () => {
    setPrimaryForm((prev) => ({
      ...prev,
      featuredProducts: [
        ...(prev.featuredProducts || []),
        { title: '', subtitle: '', description: '', image: '' },
      ],
    }));
  };

  const updateFeaturedProduct = (index, field, value) => {
    setPrimaryForm((prev) => {
      const featuredProducts = [...(prev.featuredProducts || [])];
      featuredProducts[index] = { ...featuredProducts[index], [field]: value };
      return { ...prev, featuredProducts };
    });
  };

  const removeFeaturedProduct = (index) => {
    setPrimaryForm((prev) => ({
      ...prev,
      featuredProducts: (prev.featuredProducts || []).filter((_, idx) => idx !== index),
    }));
  };

  const addImpactHighlight = () => {
    setPrimaryForm((prev) => ({
      ...prev,
      impactHighlights: [...(prev.impactHighlights || []), ''],
    }));
  };

  const updateImpactHighlight = (index, value) => {
    setPrimaryForm((prev) => {
      const highlights = [...(prev.impactHighlights || [])];
      highlights[index] = value;
      return { ...prev, impactHighlights: highlights };
    });
  };

  const removeImpactHighlight = (index) => {
    setPrimaryForm((prev) => ({
      ...prev,
      impactHighlights: (prev.impactHighlights || []).filter((_, idx) => idx !== index),
    }));
  };

  const addProfessionalSegment = () => {
    setPrimaryForm((prev) => ({
      ...prev,
      professionalSegments: [...(prev.professionalSegments || []), { title: '', desc: '' }],
    }));
  };

  const updateProfessionalSegment = (index, field, value) => {
    setPrimaryForm((prev) => {
      const segments = [...(prev.professionalSegments || [])];
      segments[index] = { ...segments[index], [field]: value };
      return { ...prev, professionalSegments: segments };
    });
  };

  const removeProfessionalSegment = (index) => {
    setPrimaryForm((prev) => ({
      ...prev,
      professionalSegments: (prev.professionalSegments || []).filter((_, idx) => idx !== index),
    }));
  };

  const addWhyChoose = () => {
    setPrimaryForm((prev) => ({
      ...prev,
      whyChoose: [...(prev.whyChoose || []), { title: '', desc: '' }],
    }));
  };

  const updateWhyChoose = (index, field, value) => {
    setPrimaryForm((prev) => {
      const reasons = [...(prev.whyChoose || [])];
      reasons[index] = { ...reasons[index], [field]: value };
      return { ...prev, whyChoose: reasons };
    });
  };

  const removeWhyChoose = (index) => {
    setPrimaryForm((prev) => ({
      ...prev,
      whyChoose: (prev.whyChoose || []).filter((_, idx) => idx !== index),
    }));
  };

  const addBlogPost = () => {
    setPrimaryForm((prev) => ({
      ...prev,
      blogPosts: [...(prev.blogPosts || []), { title: '', date: '', link: '' }],
    }));
  };

  const updateBlogPost = (index, field, value) => {
    setPrimaryForm((prev) => {
      const posts = [...(prev.blogPosts || [])];
      posts[index] = { ...posts[index], [field]: value };
      return { ...prev, blogPosts: posts };
    });
  };

  const removeBlogPost = (index) => {
    setPrimaryForm((prev) => ({
      ...prev,
      blogPosts: (prev.blogPosts || []).filter((_, idx) => idx !== index),
    }));
  };

  const updateContactInfo = (field, value) => {
    setPrimaryForm((prev) => ({
      ...prev,
      contactInfo: {
        ...(prev.contactInfo || {}),
        [field]: value,
      },
    }));
  };

  const updateContactCardInfo = (field, value) => {
    setPrimaryForm((prev) => ({
      ...prev,
      contactInfo: {
        ...(prev.contactInfo || {}),
        ctaCard: {
          ...(prev.contactInfo?.ctaCard || {}),
          [field]: value,
        },
      },
    }));
  };

  const updateAboutField = (field, value) => {
    setPrimaryForm((prev) => ({
      ...prev,
      aboutSection: {
        ...(prev.aboutSection || {}),
        [field]: value,
      },
    }));
  };

  const addAboutImageUrl = () => {
    const url = aboutImageInput.trim();
    if (!url) return;
    setPrimaryForm((prev) => ({
      ...prev,
      aboutSection: {
        ...(prev.aboutSection || {}),
        images: [...(prev.aboutSection?.images || []), url],
      },
    }));
    setAboutImageInput('');
  };

  const removeAboutImage = (index) => {
    setPrimaryForm((prev) => ({
      ...prev,
      aboutSection: {
        ...(prev.aboutSection || {}),
        images: (prev.aboutSection?.images || []).filter((_, idx) => idx !== index),
      },
    }));
  };

  const handleAboutImageUpload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploadingHint('Subiendo fotos del equipo...');
    try {
      const urls = [];
      for (const file of list) {
        const url = await uploadAsset(file, 'landing/prwd/about');
        urls.push(url);
      }
      setPrimaryForm((prev) => ({
        ...prev,
        aboutSection: {
          ...(prev.aboutSection || {}),
          images: [...(prev.aboutSection?.images || []), ...urls],
        },
      }));
    } catch (err) {
      console.error('❌ Error subiendo fotos del equipo', err);
      setPrimaryStatus('❌ No se pudieron subir las fotos del equipo.');
    } finally {
      setUploadingHint('');
    }
  };

  const handleHeroImageUpload = async (file) => {
    if (!file) return;
    setUploadingHint('Subiendo imagen principal del hero...');
    try {
      const url = await uploadAsset(file, 'landing/prwd/hero');
      handlePrimaryHeroChange('image', url);
    } catch (err) {
      console.error('❌ Error subiendo imagen del hero', err);
      setPrimaryStatus('❌ No se pudo subir la imagen del hero.');
    } finally {
      setUploadingHint('');
    }
  };

  const handleFeaturedProductImageUpload = async (index, file) => {
    if (!file) return;
    setUploadingHint('Subiendo imagen de producto destacado...');
    try {
      const product = primaryForm.featuredProducts?.[index];
      const safeName = product?.title ? product.title.toLowerCase().replace(/\s+/g, '-') : `product-${index}`;
      const url = await uploadAsset(file, `landing/prwd/featured-products/${safeName}`);
      updateFeaturedProduct(index, 'image', url);
    } catch (err) {
      console.error('❌ Error subiendo imagen de producto destacado', err);
      setPrimaryStatus('❌ No se pudo subir la imagen del producto destacado.');
    } finally {
      setUploadingHint('');
    }
  };

  const handleImpactImageUpload = async (file) => {
    if (!file) return;
    setUploadingHint('Subiendo imagen de impacto...');
    try {
      const url = await uploadAsset(file, 'landing/prwd/impact');
      updateCopy('impactSection', 'cardImage', url);
    } catch (err) {
      console.error('❌ Error subiendo imagen de impacto', err);
      setPrimaryStatus('❌ No se pudo subir la imagen de impacto.');
    } finally {
      setUploadingHint('');
    }
  };

  const handlePrimarySave = async () => {
    setPrimarySaving(true);
    setPrimaryStatus('');
    try {
      const payload = {
        siteTitle: primaryForm.siteTitle || '',
        siteName: primaryForm.siteName || defaultLandingConfig.siteName,
        metaDescription: primaryForm.metaDescription || '',
        metaKeywords: primaryForm.metaKeywords || '',
        logo: primaryForm.logo || '',
        googleTagId: primaryForm.googleTagId || defaultLandingConfig.googleTagId,
        redirectUTM: primaryForm.redirectUTM || '',
        redirectWhatsAppLink: primaryForm.redirectWhatsAppLink || '',
        servicesTitle: primaryForm.servicesTitle || '',
        whatsappPhone: primaryForm.whatsappPhone || defaultLandingConfig.whatsappPhone,
        whatsappMessage: primaryForm.whatsappMessage || defaultLandingConfig.whatsappMessage,
        hero: {
          ...(primaryForm.hero || {}),
          bullets: (primaryForm.hero?.bullets || []).filter(Boolean),
        },
        services: (primaryForm.services || []).map((service, idx) => ({
          id: service.id || randomId(`service-${idx}`),
          title: service.title || '',
          desc: service.desc || '',
          src: service.src || '',
          gallery: (service.gallery || []).filter(Boolean),
        })),
        navigation: (primaryForm.navigation || []).map((item) => ({
          label: item.label || '',
          href: item.href || '#',
          sublabel: item.sublabel || '',
        })),
        featuredProducts: (primaryForm.featuredProducts || []).map((product) => ({
          title: product.title || '',
          subtitle: product.subtitle || '',
          description: product.description || '',
          image: product.image || '',
        })),
        impactHighlights: (primaryForm.impactHighlights || []).filter(Boolean),
        professionalSegments: (primaryForm.professionalSegments || []).map((segment) => ({
          title: segment.title || '',
          desc: segment.desc || '',
        })),
        showProfessionalSection: primaryForm.showProfessionalSection !== false,
        showTestimonialSection: primaryForm.showTestimonialSection !== false,
        whyChoose: (primaryForm.whyChoose || []).map((reason) => ({
          title: reason.title || '',
          desc: reason.desc || '',
        })),
        blogPosts: (primaryForm.blogPosts || []).map((post) => ({
          title: post.title || '',
          date: post.date || '',
          link: post.link || '',
        })),
        showWhySection: primaryForm.showWhySection !== false,
        showBlogSection: primaryForm.showBlogSection !== false,
        showContactSection: primaryForm.showContactSection !== false,
        aboutSection: {
          ...(primaryForm.aboutSection || {}),
          eyebrow: primaryForm.aboutSection?.eyebrow || '',
          title: primaryForm.aboutSection?.title || '',
          subtitle: primaryForm.aboutSection?.subtitle || '',
          story: primaryForm.aboutSection?.story || '',
          images: (primaryForm.aboutSection?.images || []).filter(Boolean),
        },
        productCategories: (primaryForm.productCategories || []).map((category, idx) => ({
          id: category.id || randomId(`category-${idx}`),
          title: category.title || '',
          desc: category.desc || '',
          styles: (category.styles || []).map((style, sIdx) => ({
            id: style.id || randomId(`style-${idx}-${sIdx}`),
            name: style.name || '',
            desc: style.desc || '',
            images: (style.images || []).filter(Boolean),
          })),
        })),
        measurementGuidelines: (primaryForm.measurementGuidelines || []).map((guide, idx) => ({
          id: guide.id || randomId(`guide-${idx}`),
          title: guide.title || '',
          desc: guide.desc || '',
        })),
        quoteSteps: (primaryForm.quoteSteps || []).map((step, idx) => ({
          id: step.id || randomId(`step-${idx}`),
          title: step.title || '',
          desc: step.desc || '',
        })),
        wizardOptions: {
          colors: (primaryForm.wizardOptions?.colors || [])
            .map((opt) => ({ id: opt.id || '', label: opt.label || '' }))
            .filter((opt) => opt.id || opt.label),
          glass: (primaryForm.wizardOptions?.glass || [])
            .map((opt) => ({ id: opt.id || '', label: opt.label || '' }))
            .filter((opt) => opt.id || opt.label),
          finish: (primaryForm.wizardOptions?.finish || [])
            .map((opt) => ({ id: opt.id || '', label: opt.label || '' }))
            .filter((opt) => opt.id || opt.label),
          series: (primaryForm.wizardOptions?.series || [])
            .map((opt) => ({ id: opt.id || '', label: opt.label || '' }))
            .filter((opt) => opt.id || opt.label),
        },
        login: primaryForm.login || {},
        form: primaryForm.form || {},
        calendar: primaryForm.calendar || {},
        whatsappModal: primaryForm.whatsappModal || {},
        theme: {
          ...(defaultLandingConfig.theme || {}),
          ...(primaryForm.theme || {}),
        },
        contactInfo: {
          ...(primaryForm.contactInfo || {}),
          ctaCard: {
            ...(primaryForm.contactInfo?.ctaCard || {}),
          },
        },
        footer: primaryForm.footer || {},
        copy: primaryForm.copy || {},
      };
      const docRef = doc(db, 'config', 'landing');
      await setDoc(docRef, payload, { merge: true });
      setPrimaryStatus('✅ Landing principal actualizada.');
    } catch (err) {
      console.error('❌ Error guardando landing principal', err);
      setPrimaryStatus('❌ No se pudo guardar la configuración.');
    } finally {
      setPrimarySaving(false);
    }
  };

  const renderPrimaryEditor = () => (
    <>
      {primaryError && (
        <p className="error-message">⚠️ No se pudo cargar la landing principal. Se muestran los valores locales.</p>
      )}
      {primaryLoading && <p>Cargando datos de puertoricowindows.com...</p>}
      <div className="tab-switcher">
        {landingTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={activeTab === tab.id ? 'active' : ''}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'identity' && (
      <section className="card">
        <h3>Identidad del sitio</h3>
        <label>Título para SEO
          <input type="text" value={primaryForm.siteTitle || ''} onChange={(e) => handlePrimaryField('siteTitle', e.target.value)} />
        </label>
        <label>Nombre del sitio
          <input type="text" value={primaryForm.siteName || ''} onChange={(e) => handlePrimaryField('siteName', e.target.value)} />
        </label>
        <label>Meta descripción
          <textarea value={primaryForm.metaDescription || ''} onChange={(e) => handlePrimaryField('metaDescription', e.target.value)} />
        </label>
        <label>Meta keywords
          <textarea value={primaryForm.metaKeywords || ''} onChange={(e) => handlePrimaryField('metaKeywords', e.target.value)} />
        </label>
        <label>Logo
          <input type="text" value={primaryForm.logo || ''} onChange={(e) => handlePrimaryField('logo', e.target.value)} placeholder="https://..." />
        </label>
        <input type="file" accept="image/*" onChange={(e) => handlePrimaryLogoUpload(e.target.files?.[0])} />
        {primaryForm.logo && <img src={primaryForm.logo} alt="Logo" className="preview-image" />}
        <label>Google Tag ID
          <input type="text" value={primaryForm.googleTagId || ''} onChange={(e) => handlePrimaryField('googleTagId', e.target.value)} />
        </label>
        <div className="row two-columns">
          <label>UTM por defecto
            <input type="text" value={primaryForm.redirectUTM || ''} onChange={(e) => handlePrimaryField('redirectUTM', e.target.value)} />
          </label>
          <label>Link WhatsApp de redirección
            <input type="text" value={primaryForm.redirectWhatsAppLink || ''} onChange={(e) => handlePrimaryField('redirectWhatsAppLink', e.target.value)} />
          </label>
        </div>
      </section>
      )}

      {activeTab === 'hero' && (
      <section className="card">
        <h3>Landing principal Puerto Rico Windows & Doors</h3>
        <label>Título
          <input type="text" value={primaryForm.hero?.title || ''} onChange={(e) => handlePrimaryHeroChange('title', e.target.value)} />
        </label>
        <label>Pre título
          <input type="text" value={primaryForm.hero?.pretitle || ''} onChange={(e) => handlePrimaryHeroChange('pretitle', e.target.value)} />
        </label>
        <label>Subtítulo
          <textarea value={primaryForm.hero?.subtitle || ''} onChange={(e) => handlePrimaryHeroChange('subtitle', e.target.value)} />
        </label>
        <label>Texto CTA
          <input type="text" value={primaryForm.hero?.cta || ''} onChange={(e) => handlePrimaryHeroChange('cta', e.target.value)} />
        </label>
        <label>Bullets (uno por línea)
          <textarea
            value={(primaryForm.hero?.bullets || []).join('\n')}
            onChange={(e) => handleHeroBulletsChange(e.target.value)}
            placeholder="Beneficio 1\nBeneficio 2\nBeneficio 3"
          />
        </label>
        <label>Imagen o video de fondo
          <input type="text" value={primaryForm.hero?.image || ''} onChange={(e) => handlePrimaryHeroChange('image', e.target.value)} placeholder="https://..." />
        </label>
        <input type="file" accept="image/*,video/*" onChange={(e) => handleHeroImageUpload(e.target.files?.[0])} />
        {primaryForm.hero?.image && (
          isVideoUrl(primaryForm.hero.image) ? (
            <video src={primaryForm.hero.image} className="preview-image" controls muted />
          ) : (
            <img src={primaryForm.hero.image} alt="Hero" className="preview-image" />
          )
        )}
      </section>
      )}

      {activeTab === 'theme' && (
      <section className="card">
        <h3>Tema visual</h3>
        <div className="row two-columns">
          <label>Primary
            <input type="text" value={primaryForm.theme?.primary || ''} onChange={(e) => handleThemeChange('primary', e.target.value)} placeholder="#0f5132" />
          </label>
          <label>Primary Dark
            <input type="text" value={primaryForm.theme?.primaryDark || ''} onChange={(e) => handleThemeChange('primaryDark', e.target.value)} placeholder="#0b3d24" />
          </label>
        </div>
        <div className="row two-columns">
          <label>Accent
            <input type="text" value={primaryForm.theme?.accent || ''} onChange={(e) => handleThemeChange('accent', e.target.value)} placeholder="#1c1f23" />
          </label>
          <label>Highlight
            <input type="text" value={primaryForm.theme?.highlight || ''} onChange={(e) => handleThemeChange('highlight', e.target.value)} placeholder="#dfeee4" />
          </label>
        </div>
        <div className="row two-columns">
          <label>Gradient From
            <input type="text" value={primaryForm.theme?.gradientFrom || ''} onChange={(e) => handleThemeChange('gradientFrom', e.target.value)} placeholder="#f4f7f4" />
          </label>
          <label>Gradient To
            <input type="text" value={primaryForm.theme?.gradientTo || ''} onChange={(e) => handleThemeChange('gradientTo', e.target.value)} placeholder="#ffffff" />
          </label>
        </div>
        <div className="row two-columns">
          <label>Background
            <input type="text" value={primaryForm.theme?.background || ''} onChange={(e) => handleThemeChange('background', e.target.value)} placeholder="#f9fafb" />
          </label>
          <label>Surface
            <input type="text" value={primaryForm.theme?.surface || ''} onChange={(e) => handleThemeChange('surface', e.target.value)} placeholder="#ffffff" />
          </label>
        </div>
        <div className="row two-columns">
          <label>Surface Alterna
            <input type="text" value={primaryForm.theme?.surfaceAlt || ''} onChange={(e) => handleThemeChange('surfaceAlt', e.target.value)} placeholder="#f6faf6" />
          </label>
          <label>Card BG
            <input type="text" value={primaryForm.theme?.cardBg || ''} onChange={(e) => handleThemeChange('cardBg', e.target.value)} placeholder="#ffffff" />
          </label>
        </div>
        <div className="row two-columns">
          <label>Card Border
            <input type="text" value={primaryForm.theme?.cardBorder || ''} onChange={(e) => handleThemeChange('cardBorder', e.target.value)} placeholder="#d3e1d1" />
          </label>
          <label>Texto principal
            <input type="text" value={primaryForm.theme?.text || ''} onChange={(e) => handleThemeChange('text', e.target.value)} placeholder="#1f2937" />
          </label>
        </div>
        <div className="row two-columns">
          <label>Texto secundario
            <input type="text" value={primaryForm.theme?.muted || ''} onChange={(e) => handleThemeChange('muted', e.target.value)} placeholder="#6b7280" />
          </label>
          <label>Hero overlay from
            <input type="text" value={primaryForm.theme?.heroOverlayFrom || ''} onChange={(e) => handleThemeChange('heroOverlayFrom', e.target.value)} placeholder="#02180e" />
          </label>
        </div>
        <div className="row two-columns">
          <label>Hero overlay to
            <input type="text" value={primaryForm.theme?.heroOverlayTo || ''} onChange={(e) => handleThemeChange('heroOverlayTo', e.target.value)} placeholder="#03351d" />
          </label>
          <label>Hero text
            <input type="text" value={primaryForm.theme?.heroText || ''} onChange={(e) => handleThemeChange('heroText', e.target.value)} placeholder="#ffffff" />
          </label>
        </div>
        <div className="row two-columns">
          <label>Hero muted
            <input type="text" value={primaryForm.theme?.heroMuted || ''} onChange={(e) => handleThemeChange('heroMuted', e.target.value)} placeholder="#d1fae5" />
          </label>
          <label>Font family
            <input type="text" value={primaryForm.theme?.fontFamily || ''} onChange={(e) => handleThemeChange('fontFamily', e.target.value)} placeholder="e.g. 'Manrope', sans-serif" />
          </label>
        </div>
        <div className="row two-columns">
          <label>Hero CTA - fondo
            <input type="text" value={primaryForm.theme?.heroCtaPrimaryBg || ''} onChange={(e) => handleThemeChange('heroCtaPrimaryBg', e.target.value)} placeholder="(vacío = gradiente)" />
          </label>
          <label>Hero CTA - texto
            <input type="text" value={primaryForm.theme?.heroCtaPrimaryText || ''} onChange={(e) => handleThemeChange('heroCtaPrimaryText', e.target.value)} placeholder="#ffffff" />
          </label>
        </div>
        <div className="row two-columns">
          <label>Hero CTA secundarios - fondo
            <input type="text" value={primaryForm.theme?.heroCtaSecondaryBg || ''} onChange={(e) => handleThemeChange('heroCtaSecondaryBg', e.target.value)} placeholder="#ffffff1a" />
          </label>
          <label>Hero CTA secundarios - texto
            <input type="text" value={primaryForm.theme?.heroCtaSecondaryText || ''} onChange={(e) => handleThemeChange('heroCtaSecondaryText', e.target.value)} placeholder="#ffffff" />
          </label>
        </div>
        <label>Hero CTA secundarios - borde
          <input type="text" value={primaryForm.theme?.heroCtaSecondaryBorder || ''} onChange={(e) => handleThemeChange('heroCtaSecondaryBorder', e.target.value)} placeholder="#ffffff66" />
        </label>
        <label>Font URL (opcional)
          <input type="text" value={primaryForm.theme?.fontUrl || ''} onChange={(e) => handleThemeChange('fontUrl', e.target.value)} placeholder="https://fonts.googleapis.com/..." />
        </label>
      </section>
      )}

      {activeTab === 'labels' && (
      <section className="card">
        <h3>Textos de Header / Hero</h3>
        <label>Header - tagline
          <input
            type="text"
            value={primaryForm.copy?.header?.tagline || ''}
            onChange={(e) => updateCopy('header', 'tagline', e.target.value)}
          />
        </label>
        <label>Header - etiqueta mercados
          <input
            type="text"
            value={primaryForm.copy?.header?.marketsLabel || ''}
            onChange={(e) => updateCopy('header', 'marketsLabel', e.target.value)}
          />
        </label>
        <label>Header - label menú móvil
          <input
            type="text"
            value={primaryForm.copy?.header?.menuLabel || ''}
            onChange={(e) => updateCopy('header', 'menuLabel', e.target.value)}
          />
        </label>
        <label>Header - mercados (uno por línea)
          <textarea
            value={(primaryForm.copy?.header?.markets || []).join('\n')}
            onChange={(e) => updateCopyArray('header', 'markets', e.target.value)}
          />
        </label>
        <div className="row two-columns">
          <label>Header - botón login
            <input
              type="text"
              value={primaryForm.copy?.header?.loginLabel || ''}
              onChange={(e) => updateCopy('header', 'loginLabel', e.target.value)}
            />
          </label>
          <label>Header - botón ecommerce
            <input
              type="text"
              value={primaryForm.copy?.header?.ecommerceLabel || ''}
              onChange={(e) => updateCopy('header', 'ecommerceLabel', e.target.value)}
            />
          </label>
        </div>
        <label>Header - botón cotizar
          <input
            type="text"
            value={primaryForm.copy?.header?.quoteLabel || ''}
            onChange={(e) => updateCopy('header', 'quoteLabel', e.target.value)}
          />
        </label>
        <div className="row two-columns">
          <label>Hero - botón login
            <input
              type="text"
              value={primaryForm.copy?.hero?.loginLabel || ''}
              onChange={(e) => updateCopy('hero', 'loginLabel', e.target.value)}
            />
          </label>
          <label>Hero - botón ecommerce
            <input
              type="text"
              value={primaryForm.copy?.hero?.ecommerceLabel || ''}
              onChange={(e) => updateCopy('hero', 'ecommerceLabel', e.target.value)}
            />
          </label>
        </div>
        <label>Hero - etiqueta formulario rápido
          <input
            type="text"
            value={primaryForm.copy?.hero?.quickFormLabel || ''}
            onChange={(e) => updateCopy('hero', 'quickFormLabel', e.target.value)}
          />
        </label>
        <label>Hero - título formulario rápido
          <input
            type="text"
            value={primaryForm.copy?.hero?.quickFormTitle || ''}
            onChange={(e) => updateCopy('hero', 'quickFormTitle', e.target.value)}
          />
        </label>
        <label>Hero - botón cerrar formulario
          <input
            type="text"
            value={primaryForm.copy?.hero?.quickFormClose || ''}
            onChange={(e) => updateCopy('hero', 'quickFormClose', e.target.value)}
          />
        </label>
      </section>
      )}

      {activeTab === 'labels' && (
      <section className="card">
        <h3>Textos de Secciones</h3>
        <label>Servicios - eyebrow
          <input
            type="text"
            value={primaryForm.copy?.servicesSection?.eyebrow || ''}
            onChange={(e) => updateCopy('servicesSection', 'eyebrow', e.target.value)}
          />
        </label>
        <label>Servicios - subtítulo
          <textarea
            value={primaryForm.copy?.servicesSection?.subtitle || ''}
            onChange={(e) => updateCopy('servicesSection', 'subtitle', e.target.value)}
          />
        </label>
        <label>Servicios - CTA galería
          <input
            type="text"
            value={primaryForm.copy?.servicesSection?.galleryLabel || ''}
            onChange={(e) => updateCopy('servicesSection', 'galleryLabel', e.target.value)}
          />
        </label>
        <label>Variedad de productos - eyebrow
          <input
            type="text"
            value={primaryForm.copy?.productSection?.eyebrow || ''}
            onChange={(e) => updateCopy('productSection', 'eyebrow', e.target.value)}
          />
        </label>
        <label>Variedad de productos - título
          <input
            type="text"
            value={primaryForm.copy?.productSection?.title || ''}
            onChange={(e) => updateCopy('productSection', 'title', e.target.value)}
          />
        </label>
        <label>Variedad de productos - subtítulo
          <textarea
            value={primaryForm.copy?.productSection?.subtitle || ''}
            onChange={(e) => updateCopy('productSection', 'subtitle', e.target.value)}
          />
        </label>
        <label>Variedad - CTA galería
          <input
            type="text"
            value={primaryForm.copy?.productSection?.galleryLabel || ''}
            onChange={(e) => updateCopy('productSection', 'galleryLabel', e.target.value)}
          />
        </label>
        <label>Variedad - CTA cotizar
          <input
            type="text"
            value={primaryForm.copy?.productSection?.quoteLabel || ''}
            onChange={(e) => updateCopy('productSection', 'quoteLabel', e.target.value)}
          />
        </label>
        <label>Mediciones - eyebrow
          <input
            type="text"
            value={primaryForm.copy?.measurementSection?.eyebrow || ''}
            onChange={(e) => updateCopy('measurementSection', 'eyebrow', e.target.value)}
          />
        </label>
        <label>Mediciones - título
          <input
            type="text"
            value={primaryForm.copy?.measurementSection?.title || ''}
            onChange={(e) => updateCopy('measurementSection', 'title', e.target.value)}
          />
        </label>
        <label>Mediciones - subtítulo
          <textarea
            value={primaryForm.copy?.measurementSection?.subtitle || ''}
            onChange={(e) => updateCopy('measurementSection', 'subtitle', e.target.value)}
          />
        </label>
        <div className="row two-columns">
          <label>Mediciones - label mobile
            <input
              type="text"
              value={primaryForm.copy?.measurementSection?.mobileToggleLabel || ''}
              onChange={(e) => updateCopy('measurementSection', 'mobileToggleLabel', e.target.value)}
            />
          </label>
          <label>Mediciones - toggle open
            <input
              type="text"
              value={primaryForm.copy?.measurementSection?.mobileToggleOpen || ''}
              onChange={(e) => updateCopy('measurementSection', 'mobileToggleOpen', e.target.value)}
            />
          </label>
          <label>Mediciones - toggle close
            <input
              type="text"
              value={primaryForm.copy?.measurementSection?.mobileToggleClose || ''}
              onChange={(e) => updateCopy('measurementSection', 'mobileToggleClose', e.target.value)}
            />
          </label>
        </div>
        <label>Mediciones - info título
          <input
            type="text"
            value={primaryForm.copy?.measurementSection?.infoTitle || ''}
            onChange={(e) => updateCopy('measurementSection', 'infoTitle', e.target.value)}
          />
        </label>
        <label>Mediciones - info descripción
          <textarea
            value={primaryForm.copy?.measurementSection?.infoDescription || ''}
            onChange={(e) => updateCopy('measurementSection', 'infoDescription', e.target.value)}
          />
        </label>
        <label>Mediciones - CTA asistente
          <input
            type="text"
            value={primaryForm.copy?.measurementSection?.wizardCta || ''}
            onChange={(e) => updateCopy('measurementSection', 'wizardCta', e.target.value)}
          />
        </label>
        <label>Mediciones - nota final
          <input
            type="text"
            value={primaryForm.copy?.measurementSection?.infoFootnote || ''}
            onChange={(e) => updateCopy('measurementSection', 'infoFootnote', e.target.value)}
          />
        </label>
      </section>
      )}

      {activeTab === 'labels' && (
      <section className="card">
        <h3>Textos Adicionales</h3>
        <label>Steps - eyebrow
          <input
            type="text"
            value={primaryForm.copy?.quoteStepsSection?.eyebrow || ''}
            onChange={(e) => updateCopy('quoteStepsSection', 'eyebrow', e.target.value)}
          />
        </label>
        <label>Steps - título
          <input
            type="text"
            value={primaryForm.copy?.quoteStepsSection?.title || ''}
            onChange={(e) => updateCopy('quoteStepsSection', 'title', e.target.value)}
          />
        </label>
        <label>Steps - subtítulo
          <textarea
            value={primaryForm.copy?.quoteStepsSection?.subtitle || ''}
            onChange={(e) => updateCopy('quoteStepsSection', 'subtitle', e.target.value)}
          />
        </label>
        <label>Steps - taglines (uno por línea)
          <textarea
            value={(primaryForm.copy?.quoteStepsSection?.taglines || []).join('\n')}
            onChange={(e) => updateCopyArray('quoteStepsSection', 'taglines', e.target.value)}
          />
        </label>
        <label>Steps - CTA label
          <input
            type="text"
            value={primaryForm.copy?.quoteStepsSection?.ctaLabel || ''}
            onChange={(e) => updateCopy('quoteStepsSection', 'ctaLabel', e.target.value)}
          />
        </label>
        <label>Productos destacados - eyebrow
          <input
            type="text"
            value={primaryForm.copy?.featuredProductsSection?.eyebrow || ''}
            onChange={(e) => updateCopy('featuredProductsSection', 'eyebrow', e.target.value)}
          />
        </label>
        <label>Productos destacados - título
          <input
            type="text"
            value={primaryForm.copy?.featuredProductsSection?.title || ''}
            onChange={(e) => updateCopy('featuredProductsSection', 'title', e.target.value)}
          />
        </label>
        <label>Productos destacados - subtítulo
          <textarea
            value={primaryForm.copy?.featuredProductsSection?.subtitle || ''}
            onChange={(e) => updateCopy('featuredProductsSection', 'subtitle', e.target.value)}
          />
        </label>
        <label>Productos destacados - CTA
          <input
            type="text"
            value={primaryForm.copy?.featuredProductsSection?.ctaLabel || ''}
            onChange={(e) => updateCopy('featuredProductsSection', 'ctaLabel', e.target.value)}
          />
        </label>
        <label>Impact - eyebrow
          <input
            type="text"
            value={primaryForm.copy?.impactSection?.eyebrow || ''}
            onChange={(e) => updateCopy('impactSection', 'eyebrow', e.target.value)}
          />
        </label>
        <label>Impact - título
          <input
            type="text"
            value={primaryForm.copy?.impactSection?.title || ''}
            onChange={(e) => updateCopy('impactSection', 'title', e.target.value)}
          />
        </label>
        <label>Impact - subtítulo
          <textarea
            value={primaryForm.copy?.impactSection?.subtitle || ''}
            onChange={(e) => updateCopy('impactSection', 'subtitle', e.target.value)}
          />
        </label>
        <label>Impact - botón
          <input
            type="text"
            value={primaryForm.copy?.impactSection?.buttonLabel || ''}
            onChange={(e) => updateCopy('impactSection', 'buttonLabel', e.target.value)}
          />
        </label>
        <label>Impact - card texto
          <textarea
            value={primaryForm.copy?.impactSection?.cardText || ''}
            onChange={(e) => updateCopy('impactSection', 'cardText', e.target.value)}
          />
        </label>
        <label>Impact - card imagen URL
          <input
            type="text"
            value={primaryForm.copy?.impactSection?.cardImage || ''}
            onChange={(e) => updateCopy('impactSection', 'cardImage', e.target.value)}
          />
        </label>
        <label className="upload-label">
          Subir imagen de impacto
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              handleImpactImageUpload(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </label>
        {primaryForm.copy?.impactSection?.cardImage && (
          <img
            src={primaryForm.copy.impactSection.cardImage}
            alt="Impact preview"
            className="preview-image"
          />
        )}
        <label>Impact - card alt
          <input
            type="text"
            value={primaryForm.copy?.impactSection?.cardAlt || ''}
            onChange={(e) => updateCopy('impactSection', 'cardAlt', e.target.value)}
          />
        </label>
        <label>Profesionales - eyebrow
          <input
            type="text"
            value={primaryForm.copy?.professionalSection?.eyebrow || ''}
            onChange={(e) => updateCopy('professionalSection', 'eyebrow', e.target.value)}
          />
        </label>
        <label>Profesionales - título
          <input
            type="text"
            value={primaryForm.copy?.professionalSection?.title || ''}
            onChange={(e) => updateCopy('professionalSection', 'title', e.target.value)}
          />
        </label>
        <div className="row two-columns">
          <label>Profesionales - CTA prefix
            <input
              type="text"
              value={primaryForm.copy?.professionalSection?.ctaLabelPrefix || ''}
              onChange={(e) => updateCopy('professionalSection', 'ctaLabelPrefix', e.target.value)}
            />
          </label>
          <label>Profesionales - CTA suffix
            <input
              type="text"
              value={primaryForm.copy?.professionalSection?.ctaLabelSuffix || ''}
              onChange={(e) => updateCopy('professionalSection', 'ctaLabelSuffix', e.target.value)}
            />
          </label>
        </div>
        <label>Testimonial - eyebrow
          <input
            type="text"
            value={primaryForm.copy?.testimonialSection?.eyebrow || ''}
            onChange={(e) => updateCopy('testimonialSection', 'eyebrow', e.target.value)}
          />
        </label>
        <label className="flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--admin-text)' }}>
          <input
            type="checkbox"
            checked={primaryForm.showTestimonialSection !== false}
            onChange={(e) => handlePrimaryField('showTestimonialSection', e.target.checked)}
          />
          Mostrar sección de testimonio
        </label>
        <label>Testimonial - cita
          <textarea
            value={primaryForm.copy?.testimonialSection?.quote || ''}
            onChange={(e) => updateCopy('testimonialSection', 'quote', e.target.value)}
          />
        </label>
        <label>Testimonial - autor
          <input
            type="text"
            value={primaryForm.copy?.testimonialSection?.author || ''}
            onChange={(e) => updateCopy('testimonialSection', 'author', e.target.value)}
          />
        </label>
        <label>Why - eyebrow
          <input
            type="text"
            value={primaryForm.copy?.whySection?.eyebrow || ''}
            onChange={(e) => updateCopy('whySection', 'eyebrow', e.target.value)}
          />
        </label>
        <label>Why - título
          <input
            type="text"
            value={primaryForm.copy?.whySection?.title || ''}
            onChange={(e) => updateCopy('whySection', 'title', e.target.value)}
          />
        </label>
        <label>Blog - eyebrow
          <input
            type="text"
            value={primaryForm.copy?.blogSection?.eyebrow || ''}
            onChange={(e) => updateCopy('blogSection', 'eyebrow', e.target.value)}
          />
        </label>
        <label>Blog - título
          <input
            type="text"
            value={primaryForm.copy?.blogSection?.title || ''}
            onChange={(e) => updateCopy('blogSection', 'title', e.target.value)}
          />
        </label>
        <label>Blog - CTA
          <input
            type="text"
            value={primaryForm.copy?.blogSection?.ctaLabel || ''}
            onChange={(e) => updateCopy('blogSection', 'ctaLabel', e.target.value)}
          />
        </label>
        <div className="row two-columns">
          <label>Footer - título navegación
            <input
              type="text"
              value={primaryForm.copy?.footerSection?.navTitle || ''}
              onChange={(e) => updateCopy('footerSection', 'navTitle', e.target.value)}
            />
          </label>
          <label>Footer - título contacto
            <input
              type="text"
              value={primaryForm.copy?.footerSection?.contactTitle || ''}
              onChange={(e) => updateCopy('footerSection', 'contactTitle', e.target.value)}
            />
          </label>
          <label>Footer - texto derechos
            <input
              type="text"
              value={primaryForm.copy?.footerSection?.rightsText || ''}
              onChange={(e) => updateCopy('footerSection', 'rightsText', e.target.value)}
            />
          </label>
        </div>
        <label>Contacto - eyebrow
          <input
            type="text"
            value={primaryForm.copy?.contactSection?.eyebrow || ''}
            onChange={(e) => updateCopy('contactSection', 'eyebrow', e.target.value)}
          />
        </label>
        <div className="row two-columns">
          <label>Contacto - label email
            <input
              type="text"
              value={primaryForm.copy?.contactSection?.labels?.email || ''}
              onChange={(e) => updateCopyNested('contactSection', 'labels', 'email', e.target.value)}
            />
          </label>
          <label>Contacto - label whatsapp
            <input
              type="text"
              value={primaryForm.copy?.contactSection?.labels?.whatsapp || ''}
              onChange={(e) => updateCopyNested('contactSection', 'labels', 'whatsapp', e.target.value)}
            />
          </label>
          <label>Contacto - label horario
            <input
              type="text"
              value={primaryForm.copy?.contactSection?.labels?.hours || ''}
              onChange={(e) => updateCopyNested('contactSection', 'labels', 'hours', e.target.value)}
            />
          </label>
          <label>Contacto - label regiones
            <input
              type="text"
              value={primaryForm.copy?.contactSection?.labels?.location || ''}
              onChange={(e) => updateCopyNested('contactSection', 'labels', 'location', e.target.value)}
            />
          </label>
        </div>
        <label>Contacto - nota final
          <input
            type="text"
            value={primaryForm.copy?.contactSection?.ctaFootnote || ''}
            onChange={(e) => updateCopy('contactSection', 'ctaFootnote', e.target.value)}
          />
        </label>
      </section>
      )}

      {activeTab === 'labels' && (
      <section className="card">
        <h3>Modales, Wizard y Asistente</h3>
        <label>Wizard - título
          <input
            type="text"
            value={primaryForm.copy?.wizard?.title || ''}
            onChange={(e) => updateCopy('wizard', 'title', e.target.value)}
          />
        </label>
        <label>Wizard - subtítulo
          <input
            type="text"
            value={primaryForm.copy?.wizard?.subtitle || ''}
            onChange={(e) => updateCopy('wizard', 'subtitle', e.target.value)}
          />
        </label>
        <label>Wizard - descripción
          <textarea
            value={primaryForm.copy?.wizard?.description || ''}
            onChange={(e) => updateCopy('wizard', 'description', e.target.value)}
          />
        </label>
        <label>Wizard - steps (uno por línea)
          <textarea
            value={(primaryForm.copy?.wizard?.steps || []).join('\n')}
            onChange={(e) => updateCopyArray('wizard', 'steps', e.target.value)}
          />
        </label>
        <label>Wizard - hint paso 2
          <input
            type="text"
            value={primaryForm.copy?.wizard?.step2Hint || ''}
            onChange={(e) => updateCopy('wizard', 'step2Hint', e.target.value)}
          />
        </label>
        <label>Wizard - label seleccionado
          <input
            type="text"
            value={primaryForm.copy?.wizard?.selectedLabel || ''}
            onChange={(e) => updateCopy('wizard', 'selectedLabel', e.target.value)}
          />
        </label>
        <label>Wizard - texto sin estilos
          <input
            type="text"
            value={primaryForm.copy?.wizard?.noStylesText || ''}
            onChange={(e) => updateCopy('wizard', 'noStylesText', e.target.value)}
          />
        </label>
        <div className="row two-columns">
          <label>Wizard - label color
            <input
              type="text"
              value={primaryForm.copy?.wizard?.step3ColorLabel || ''}
              onChange={(e) => updateCopy('wizard', 'step3ColorLabel', e.target.value)}
            />
          </label>
          <label>Wizard - label cristal
            <input
              type="text"
              value={primaryForm.copy?.wizard?.step3GlassLabel || ''}
              onChange={(e) => updateCopy('wizard', 'step3GlassLabel', e.target.value)}
            />
          </label>
          <label>Wizard - label acabado
            <input
              type="text"
              value={primaryForm.copy?.wizard?.step3FinishLabel || ''}
              onChange={(e) => updateCopy('wizard', 'step3FinishLabel', e.target.value)}
            />
          </label>
        </div>
        <div className="row two-columns">
          <label>Wizard - label alto
            <input
              type="text"
              value={primaryForm.copy?.wizard?.heightLabel || ''}
              onChange={(e) => updateCopy('wizard', 'heightLabel', e.target.value)}
            />
          </label>
          <label>Wizard - label ancho
            <input
              type="text"
              value={primaryForm.copy?.wizard?.widthLabel || ''}
              onChange={(e) => updateCopy('wizard', 'widthLabel', e.target.value)}
            />
          </label>
          <label>Wizard - label cantidad
            <input
              type="text"
              value={primaryForm.copy?.wizard?.quantityLabel || ''}
              onChange={(e) => updateCopy('wizard', 'quantityLabel', e.target.value)}
            />
          </label>
          <label>Wizard - label serie
            <input
              type="text"
              value={primaryForm.copy?.wizard?.seriesLabel || ''}
              onChange={(e) => updateCopy('wizard', 'seriesLabel', e.target.value)}
            />
          </label>
        </div>
        <label>Wizard - helper resumen
          <input
            type="text"
            value={primaryForm.copy?.wizard?.summaryHelper || ''}
            onChange={(e) => updateCopy('wizard', 'summaryHelper', e.target.value)}
          />
        </label>
        <div className="row two-columns">
          <label>Wizard - label medidas
            <input
              type="text"
              value={primaryForm.copy?.wizard?.summaryMeasurementsLabel || ''}
              onChange={(e) => updateCopy('wizard', 'summaryMeasurementsLabel', e.target.value)}
            />
          </label>
          <label>Wizard - label serie resumen
            <input
              type="text"
              value={primaryForm.copy?.wizard?.summarySeriesLabel || ''}
              onChange={(e) => updateCopy('wizard', 'summarySeriesLabel', e.target.value)}
            />
          </label>
        </div>
        <label>Wizard - label acabados resumen
          <input
            type="text"
            value={primaryForm.copy?.wizard?.summaryFinishLabel || ''}
            onChange={(e) => updateCopy('wizard', 'summaryFinishLabel', e.target.value)}
          />
        </label>
        <div className="row two-columns">
          <label>Wizard - unidad resumen
            <input
              type="text"
              value={primaryForm.copy?.wizard?.summaryUnitLabel || ''}
              onChange={(e) => updateCopy('wizard', 'summaryUnitLabel', e.target.value)}
            />
          </label>
          <label>Wizard - valor vacío resumen
            <input
              type="text"
              value={primaryForm.copy?.wizard?.summaryEmptyValue || ''}
              onChange={(e) => updateCopy('wizard', 'summaryEmptyValue', e.target.value)}
            />
          </label>
        </div>
        <label>Wizard - título fallback resumen
          <input
            type="text"
            value={primaryForm.copy?.wizard?.summaryTitleFallback || ''}
            onChange={(e) => updateCopy('wizard', 'summaryTitleFallback', e.target.value)}
          />
        </label>
        <div className="row two-columns">
          <label>Wizard - botón atrás
            <input
              type="text"
              value={primaryForm.copy?.wizard?.prevLabel || ''}
              onChange={(e) => updateCopy('wizard', 'prevLabel', e.target.value)}
            />
          </label>
          <label>Wizard - botón siguiente
            <input
              type="text"
              value={primaryForm.copy?.wizard?.nextLabel || ''}
              onChange={(e) => updateCopy('wizard', 'nextLabel', e.target.value)}
            />
          </label>
          <label>Wizard - botón finalizar
            <input
              type="text"
              value={primaryForm.copy?.wizard?.finishLabel || ''}
              onChange={(e) => updateCopy('wizard', 'finishLabel', e.target.value)}
            />
          </label>
          <label>Wizard - botón cerrar
            <input
              type="text"
              value={primaryForm.copy?.wizard?.closeLabel || ''}
              onChange={(e) => updateCopy('wizard', 'closeLabel', e.target.value)}
            />
          </label>
        </div>
        <h4>Opciones Wizard (id|label por línea)</h4>
        <label>Wizard - colores
          <textarea
            value={formatWizardOptions(primaryForm.wizardOptions?.colors)}
            onChange={(e) => updateWizardOptions('colors', e.target.value)}
          />
        </label>
        <label>Wizard - cristales
          <textarea
            value={formatWizardOptions(primaryForm.wizardOptions?.glass)}
            onChange={(e) => updateWizardOptions('glass', e.target.value)}
          />
        </label>
        <label>Wizard - acabados
          <textarea
            value={formatWizardOptions(primaryForm.wizardOptions?.finish)}
            onChange={(e) => updateWizardOptions('finish', e.target.value)}
          />
        </label>
        <label>Wizard - series
          <textarea
            value={formatWizardOptions(primaryForm.wizardOptions?.series)}
            onChange={(e) => updateWizardOptions('series', e.target.value)}
          />
        </label>
        <label>Modal galería - label
          <input
            type="text"
            value={primaryForm.copy?.modals?.galleryLabel || ''}
            onChange={(e) => updateCopy('modals', 'galleryLabel', e.target.value)}
          />
        </label>
        <label>Modal galería - CTA
          <input
            type="text"
            value={primaryForm.copy?.modals?.galleryCta || ''}
            onChange={(e) => updateCopy('modals', 'galleryCta', e.target.value)}
          />
        </label>
        <label>Modal galería - cerrar
          <input
            type="text"
            value={primaryForm.copy?.modals?.galleryClose || ''}
            onChange={(e) => updateCopy('modals', 'galleryClose', e.target.value)}
          />
        </label>
        <div className="row two-columns">
          <label>Modal galería - anterior
            <input
              type="text"
              value={primaryForm.copy?.modals?.galleryPrevLabel || ''}
              onChange={(e) => updateCopy('modals', 'galleryPrevLabel', e.target.value)}
            />
          </label>
          <label>Modal galería - siguiente
            <input
              type="text"
              value={primaryForm.copy?.modals?.galleryNextLabel || ''}
              onChange={(e) => updateCopy('modals', 'galleryNextLabel', e.target.value)}
            />
          </label>
        </div>
        <label>Modal cotización - label
          <input
            type="text"
            value={primaryForm.copy?.modals?.quoteModalLabel || ''}
            onChange={(e) => updateCopy('modals', 'quoteModalLabel', e.target.value)}
          />
        </label>
        <label>Modal cotización - guardar
          <input
            type="text"
            value={primaryForm.copy?.modals?.quoteModalSave || ''}
            onChange={(e) => updateCopy('modals', 'quoteModalSave', e.target.value)}
          />
        </label>
        <label>Modal cotización - cancelar
          <input
            type="text"
            value={primaryForm.copy?.modals?.quoteModalCancel || ''}
            onChange={(e) => updateCopy('modals', 'quoteModalCancel', e.target.value)}
          />
        </label>
        <div className="row two-columns">
          <label>Modal cotización - cerrar
            <input
              type="text"
              value={primaryForm.copy?.modals?.quoteModalCloseLabel || ''}
              onChange={(e) => updateCopy('modals', 'quoteModalCloseLabel', e.target.value)}
            />
          </label>
          <label>Modal cotización - error
            <input
              type="text"
              value={primaryForm.copy?.modals?.quoteModalError || ''}
              onChange={(e) => updateCopy('modals', 'quoteModalError', e.target.value)}
            />
          </label>
        </div>
        <label>Asistente - botón flotante
          <input
            type="text"
            value={primaryForm.copy?.assistant?.buttonLabel || ''}
            onChange={(e) => updateCopy('assistant', 'buttonLabel', e.target.value)}
          />
        </label>
        <label>Asistente - label
          <input
            type="text"
            value={primaryForm.copy?.assistant?.label || ''}
            onChange={(e) => updateCopy('assistant', 'label', e.target.value)}
          />
        </label>
        <label>Asistente - título
          <input
            type="text"
            value={primaryForm.copy?.assistant?.title || ''}
            onChange={(e) => updateCopy('assistant', 'title', e.target.value)}
          />
        </label>
        <label>Asistente - saludo inicial
          <textarea
            value={primaryForm.copy?.assistant?.greeting || ''}
            onChange={(e) => updateCopy('assistant', 'greeting', e.target.value)}
          />
        </label>
        <div className="row two-columns">
          <label>Asistente - modo compacto
            <input
              type="text"
              value={primaryForm.copy?.assistant?.compactLabel || ''}
              onChange={(e) => updateCopy('assistant', 'compactLabel', e.target.value)}
            />
          </label>
          <label>Asistente - modo amplio
            <input
              type="text"
              value={primaryForm.copy?.assistant?.wideLabel || ''}
              onChange={(e) => updateCopy('assistant', 'wideLabel', e.target.value)}
            />
          </label>
        </div>
        <label>Asistente - placeholder input
          <input
            type="text"
            value={primaryForm.copy?.assistant?.inputPlaceholder || ''}
            onChange={(e) => updateCopy('assistant', 'inputPlaceholder', e.target.value)}
          />
        </label>
        <div className="row two-columns">
          <label>Asistente - botón enviar
            <input
              type="text"
              value={primaryForm.copy?.assistant?.sendLabel || ''}
              onChange={(e) => updateCopy('assistant', 'sendLabel', e.target.value)}
            />
          </label>
          <label>Asistente - estado loading
            <input
              type="text"
              value={primaryForm.copy?.assistant?.loadingLabel || ''}
              onChange={(e) => updateCopy('assistant', 'loadingLabel', e.target.value)}
            />
          </label>
        </div>
        <label>Asistente - respuesta fallback
          <textarea
            value={primaryForm.copy?.assistant?.fallbackReply || ''}
            onChange={(e) => updateCopy('assistant', 'fallbackReply', e.target.value)}
          />
        </label>
        <label>Asistente - respuesta error
          <textarea
            value={primaryForm.copy?.assistant?.errorReply || ''}
            onChange={(e) => updateCopy('assistant', 'errorReply', e.target.value)}
          />
        </label>
        <label>Asistente - aviso error
          <input
            type="text"
            value={primaryForm.copy?.assistant?.errorNotice || ''}
            onChange={(e) => updateCopy('assistant', 'errorNotice', e.target.value)}
          />
        </label>
        <label>Botón flotante cotización
          <input
            type="text"
            value={primaryForm.copy?.floatingQuoteButton || ''}
            onChange={(e) => updateCopyRoot('floatingQuoteButton', e.target.value)}
          />
        </label>
      </section>
      )}
      {activeTab === 'content' && (
      <section className="card">
        <div className="card-header">
          <h3>Navegación principal</h3>
          <button type="button" onClick={addNavigationItem}>+ Agregar enlace</button>
        </div>
        {(primaryForm.navigation || []).map((item, index) => (
          <div key={`nav-${index}`} className="feature-block">
            <div className="row two-columns">
              <label>Título
                <input
                  type="text"
                  value={item.label || ''}
                  onChange={(e) => updateNavigationItem(index, 'label', e.target.value)}
                />
              </label>
              <label>Subtítulo
                <input
                  type="text"
                  value={item.sublabel || ''}
                  onChange={(e) => updateNavigationItem(index, 'sublabel', e.target.value)}
                />
              </label>
            </div>
            <label>Enlace
              <input
                type="text"
                value={item.href || ''}
                onChange={(e) => updateNavigationItem(index, 'href', e.target.value)}
                placeholder="#products"
              />
            </label>
            <button type="button" className="btn-delete" onClick={() => removeNavigationItem(index)}>
              Eliminar enlace
            </button>
          </div>
        ))}
      </section>
      )}

      {activeTab === 'content' && (
      <section className="card">
        <div className="card-header">
          <h3>Productos destacados (hero secundario)</h3>
          <button type="button" onClick={addFeaturedProduct}>+ Agregar tarjeta</button>
        </div>
        {(primaryForm.featuredProducts || []).map((product, index) => (
          <div key={`featured-${index}`} className="feature-block">
            <div className="row two-columns">
              <label>Título
                <input
                  type="text"
                  value={product.title || ''}
                  onChange={(e) => updateFeaturedProduct(index, 'title', e.target.value)}
                />
              </label>
              <label>Subtítulo
                <input
                  type="text"
                  value={product.subtitle || ''}
                  onChange={(e) => updateFeaturedProduct(index, 'subtitle', e.target.value)}
                />
              </label>
            </div>
            <label>Descripción
              <textarea
                value={product.description || ''}
                onChange={(e) => updateFeaturedProduct(index, 'description', e.target.value)}
              />
            </label>
            <label>Imagen
              <input
                type="text"
                value={product.image || ''}
                onChange={(e) => updateFeaturedProduct(index, 'image', e.target.value)}
                placeholder="https://..."
              />
            </label>
            <label className="upload-label">
              Subir imagen
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  handleFeaturedProductImageUpload(index, e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>
            {product.image && <img src={product.image} alt={product.title || 'Producto'} className="preview-image" />}
            <button type="button" className="btn-delete" onClick={() => removeFeaturedProduct(index)}>
              Eliminar tarjeta
            </button>
          </div>
        ))}
      </section>
      )}

      {activeTab === 'services' && (
      <section className="card">
        <div className="card-header">
          <h3>Servicios destacados</h3>
          <button type="button" onClick={addPrimaryService}>+ Agregar servicio</button>
        </div>
        <label>Título de la sección
          <input type="text" value={primaryForm.servicesTitle || ''} onChange={(e) => handlePrimaryField('servicesTitle', e.target.value)} />
        </label>
        {(primaryForm.services || []).map((service, index) => (
          <div key={service.id || index} className="feature-block">
            <div className="row two-columns">
              <label>Título
                <input type="text" value={service.title || ''} onChange={(e) => updatePrimaryService(index, 'title', e.target.value)} />
              </label>
              <label>Imagen principal
                <input type="text" value={service.src || ''} onChange={(e) => updatePrimaryService(index, 'src', e.target.value)} placeholder="https://..." />
              </label>
            </div>
            <input type="file" accept="image/*" onChange={(e) => handlePrimaryServiceImageUpload(index, e.target.files?.[0])} />
            {service.src && <img src={service.src} alt={service.title || 'Servicio'} className="preview-image" />}
            <label>Descripción
              <textarea value={service.desc || ''} onChange={(e) => updatePrimaryService(index, 'desc', e.target.value)} />
            </label>
            <div className="gallery-control">
              <label>Galería del servicio</label>
              <div className="gallery-url-input">
                <input
                  type="text"
                  value={serviceGalleryInputs[getServiceKey(service, index)] || ''}
                  onChange={(e) => handleServiceGalleryUrlChange(getServiceKey(service, index), e.target.value)}
                  placeholder="https://mi-imagen.jpg"
                />
                <button type="button" onClick={() => addServiceGalleryUrl(index)}>
                  Agregar URL
                </button>
              </div>
            </div>
            {service.gallery?.length > 0 && (
              <div className="gallery-grid">
                {service.gallery.map((item, i) => {
                  const video = isVideoUrl(item);
                  const pdf = isPdfUrl(item);
                  return (
                    <div key={`${item}-${i}`} className={`gallery-item${pdf ? ' gallery-item-doc' : ''}`}>
                      {video ? (
                        <video src={item} controls className="gallery-video" />
                      ) : pdf ? (
                        <div className="doc-preview">
                          <span>PDF</span>
                          <a href={item} target="_blank" rel="noopener noreferrer">Ver archivo</a>
                        </div>
                      ) : (
                        <img src={item} alt={`gallery-${i}`} />
                      )}
                      <button type="button" onClick={() => removePrimaryServiceGalleryItem(index, i)}>×</button>
                    </div>
                  );
                })}
              </div>
            )}
            <label className="upload-label">
              Subir archivos
              <input
                type="file"
                accept="image/*,video/*,application/pdf"
                multiple
                onChange={(e) => handlePrimaryServiceGalleryUpload(index, e.target.files)}
              />
            </label>
            <button type="button" className="btn-delete" onClick={() => removePrimaryService(index)}>Eliminar servicio</button>
          </div>
        ))}
      </section>
      )}

      {activeTab === 'content' && (
      <section className="card">
        <div className="card-header">
          <h3>Impact highlights</h3>
          <button type="button" onClick={addImpactHighlight}>+ Agregar bullet</button>
        </div>
        {(primaryForm.impactHighlights || []).map((highlight, index) => (
          <div key={`impact-${index}`} className="feature-block">
            <textarea
              value={highlight || ''}
              onChange={(e) => updateImpactHighlight(index, e.target.value)}
              placeholder="Descripción del beneficio"
            />
            <button type="button" className="btn-delete" onClick={() => removeImpactHighlight(index)}>
              Eliminar
            </button>
          </div>
        ))}
      </section>
      )}

      {activeTab === 'content' && (
      <section className="card">
        <div className="card-header">
          <h3>Segmentos profesionales</h3>
          <button type="button" onClick={addProfessionalSegment}>+ Agregar segmento</button>
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--admin-text)' }}>
          <input
            type="checkbox"
            checked={primaryForm.showProfessionalSection !== false}
            onChange={(e) => handlePrimaryField('showProfessionalSection', e.target.checked)}
          />
          Mostrar sección de profesionales
        </label>
        {(primaryForm.professionalSegments || []).map((segment, index) => (
          <div key={`segment-${index}`} className="feature-block">
            <label>Título
              <input
                type="text"
                value={segment.title || ''}
                onChange={(e) => updateProfessionalSegment(index, 'title', e.target.value)}
              />
            </label>
            <label>Descripción
              <textarea
                value={segment.desc || ''}
                onChange={(e) => updateProfessionalSegment(index, 'desc', e.target.value)}
              />
            </label>
            <button type="button" className="btn-delete" onClick={() => removeProfessionalSegment(index)}>
              Eliminar segmento
            </button>
          </div>
        ))}
      </section>
      )}

      {activeTab === 'content' && (
      <section className="card">
        <div className="card-header">
          <h3>Razones para elegirnos</h3>
          <button type="button" onClick={addWhyChoose}>+ Agregar razón</button>
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--admin-text)' }}>
          <input
            type="checkbox"
            checked={primaryForm.showWhySection !== false}
            onChange={(e) => handlePrimaryField('showWhySection', e.target.checked)}
          />
          Mostrar sección Por qué elegirnos
        </label>
        {(primaryForm.whyChoose || []).map((reason, index) => (
          <div key={`why-${index}`} className="testimonial-block">
            <label>Título
              <input
                type="text"
                value={reason.title || ''}
                onChange={(e) => updateWhyChoose(index, 'title', e.target.value)}
              />
            </label>
            <label>Descripción
              <textarea
                value={reason.desc || ''}
                onChange={(e) => updateWhyChoose(index, 'desc', e.target.value)}
              />
            </label>
            <button type="button" className="btn-delete" onClick={() => removeWhyChoose(index)}>
              Eliminar razón
            </button>
          </div>
        ))}
      </section>
      )}

      {activeTab === 'content' && (
      <section className="card">
        <div className="card-header">
          <h3>Blog / Recursos</h3>
          <button type="button" onClick={addBlogPost}>+ Agregar artículo</button>
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--admin-text)' }}>
          <input
            type="checkbox"
            checked={primaryForm.showBlogSection !== false}
            onChange={(e) => handlePrimaryField('showBlogSection', e.target.checked)}
          />
          Mostrar sección Recursos
        </label>
        {(primaryForm.blogPosts || []).map((post, index) => (
          <div key={`blog-${index}`} className="feature-block">
            <div className="row two-columns">
              <label>Título
                <input
                  type="text"
                  value={post.title || ''}
                  onChange={(e) => updateBlogPost(index, 'title', e.target.value)}
                />
              </label>
              <label>Fecha
                <input
                  type="text"
                  value={post.date || ''}
                  onChange={(e) => updateBlogPost(index, 'date', e.target.value)}
                />
              </label>
            </div>
            <label>Enlace
              <input
                type="text"
                value={post.link || ''}
                onChange={(e) => updateBlogPost(index, 'link', e.target.value)}
                placeholder="https://..."
              />
            </label>
            <button type="button" className="btn-delete" onClick={() => removeBlogPost(index)}>
              Eliminar artículo
            </button>
          </div>
        ))}
      </section>
      )}

      {activeTab === 'about' && (
      <section className="card">
        <div className="card-header">
          <h3>About Us</h3>
        </div>
        <label>Eyebrow
          <input
            type="text"
            value={primaryForm.aboutSection?.eyebrow || ''}
            onChange={(e) => updateAboutField('eyebrow', e.target.value)}
          />
        </label>
        <label>Título
          <input
            type="text"
            value={primaryForm.aboutSection?.title || ''}
            onChange={(e) => updateAboutField('title', e.target.value)}
          />
        </label>
        <label>Subtítulo
          <input
            type="text"
            value={primaryForm.aboutSection?.subtitle || ''}
            onChange={(e) => updateAboutField('subtitle', e.target.value)}
          />
        </label>
        <label>Historia
          <textarea
            value={primaryForm.aboutSection?.story || ''}
            onChange={(e) => updateAboutField('story', e.target.value)}
          />
        </label>
        <div className="gallery-control">
          <label>Fotos del equipo (URLs)</label>
          <div className="gallery-url-input">
            <input
              type="text"
              value={aboutImageInput}
              onChange={(e) => setAboutImageInput(e.target.value)}
              placeholder="https://mi-imagen.jpg"
            />
            <button type="button" onClick={addAboutImageUrl}>
              Agregar URL
            </button>
          </div>
        </div>
        {primaryForm.aboutSection?.images?.length > 0 && (
          <div className="gallery-grid">
            {primaryForm.aboutSection.images.map((img, idx) => (
              <div key={`${img}-${idx}`} className="gallery-item">
                <img src={img} alt={`about-${idx}`} />
                <button type="button" onClick={() => removeAboutImage(idx)}>×</button>
              </div>
            ))}
          </div>
        )}
        <label className="upload-label">
          Subir fotos del equipo
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => {
              handleAboutImageUpload(e.target.files);
              e.target.value = '';
            }}
          />
        </label>
      </section>
      )}

      {activeTab === 'catalog' && (
      <section className="card">
        <div className="card-header">
          <h3>Variedad de productos (Ventanas · Puertas · Garage)</h3>
          <button type="button" onClick={addProductCategory}>+ Agregar categoría</button>
        </div>
        <p className="text-sm text-gray-600">
          Configura las tarjetas que aparecen en “Variedad completa”. Cada categoría puede tener múltiples estilos con su galería.
        </p>
        {(primaryForm.productCategories || []).map((category, catIndex) => (
          <div key={category.id || catIndex} className="feature-block">
            <div className="row two-columns">
              <label>Nombre de la categoría
                <input
                  type="text"
                  value={category.title || ''}
                  onChange={(e) => updateProductCategory(catIndex, 'title', e.target.value)}
                />
              </label>
              <label>Descripción breve
                <input
                  type="text"
                  value={category.desc || ''}
                  onChange={(e) => updateProductCategory(catIndex, 'desc', e.target.value)}
                />
              </label>
            </div>
            <div className="card-header" style={{ marginTop: '1rem' }}>
              <h4>Estilos</h4>
              <button type="button" onClick={() => addCategoryStyle(catIndex)}>+ Agregar estilo</button>
            </div>
            {(category.styles || []).map((style, styleIndex) => (
              <div key={style.id || styleIndex} className="testimonial-block">
                <div className="row two-columns">
                  <label>Nombre del estilo
                    <input
                      type="text"
                      value={style.name || ''}
                      onChange={(e) => updateCategoryStyle(catIndex, styleIndex, 'name', e.target.value)}
                    />
                  </label>
                  <label>Descripción
                    <input
                      type="text"
                      value={style.desc || ''}
                      onChange={(e) => updateCategoryStyle(catIndex, styleIndex, 'desc', e.target.value)}
                    />
                  </label>
                </div>
                <div className="gallery-grid">
                  {(style.images || []).map((img, imgIndex) => (
                    <div key={`${img}-${imgIndex}`} className="gallery-item">
                      <img src={img} alt={`${style.name || 'style'}-${imgIndex}`} />
                      <button type="button" onClick={() => removeStyleImage(catIndex, styleIndex, imgIndex)}>×</button>
                    </div>
                  ))}
                </div>
                <div className="gallery-url-input">
                  <input
                    type="text"
                    value={styleGalleryInputs[getStyleKey(catIndex, styleIndex)] || ''}
                    onChange={(e) =>
                      setStyleGalleryInputs((prev) => ({
                        ...prev,
                        [getStyleKey(catIndex, styleIndex)]: e.target.value,
                      }))
                    }
                    placeholder="https://mi-imagen.jpg"
                  />
                  <button type="button" onClick={() => handleStyleImageUrlAdd(catIndex, styleIndex)}>
                    Agregar URL
                  </button>
                </div>
                <label className="upload-label">
                  Subir imagen
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleStyleImageUpload(catIndex, styleIndex, e.target.files?.[0])}
                  />
                </label>
                <button
                  type="button"
                  className="btn-delete"
                  onClick={() => removeCategoryStyle(catIndex, styleIndex)}
                >
                  Eliminar estilo
                </button>
              </div>
            ))}
            <button type="button" className="btn-delete" onClick={() => removeProductCategory(catIndex)}>
              Eliminar categoría
            </button>
          </div>
        ))}
      </section>
      )}

      {activeTab === 'guides' && (
      <section className="card">
        <div className="card-header">
          <h3>Instrucciones de medición</h3>
          <button type="button" onClick={addMeasurementGuide}>+ Agregar instrucción</button>
        </div>
        {(primaryForm.measurementGuidelines || []).map((guide, index) => (
          <div key={guide.id || index} className="feature-block">
            <label>Título
              <input
                type="text"
                value={guide.title || ''}
                onChange={(e) => updateMeasurementGuide(index, 'title', e.target.value)}
              />
            </label>
            <label>Descripción
              <textarea
                value={guide.desc || ''}
                onChange={(e) => updateMeasurementGuide(index, 'desc', e.target.value)}
              />
            </label>
            <button type="button" className="btn-delete" onClick={() => removeMeasurementGuide(index)}>
              Eliminar
            </button>
          </div>
        ))}
      </section>
      )}

      {activeTab === 'guides' && (
      <section className="card">
        <div className="card-header">
          <h3>Paso a paso del cotizador</h3>
          <button type="button" onClick={addQuoteStep}>+ Agregar paso</button>
        </div>
        {(primaryForm.quoteSteps || []).map((step, index) => (
          <div key={step.id || index} className="testimonial-block">
            <label>Título
              <input
                type="text"
                value={step.title || ''}
                onChange={(e) => updateQuoteStep(index, 'title', e.target.value)}
              />
            </label>
            <label>Descripción
              <textarea
                value={step.desc || ''}
                onChange={(e) => updateQuoteStep(index, 'desc', e.target.value)}
              />
            </label>
            <button type="button" className="btn-delete" onClick={() => removeQuoteStep(index)}>
              Eliminar
            </button>
          </div>
        ))}
      </section>
      )}

      {activeTab === 'aux' && (
      <section className="card">
        <h3>Textos auxiliares</h3>
        <div className="row two-columns">
          <label>Login - texto
            <input type="text" value={primaryForm.login?.text || ''} onChange={(e) => handlePrimaryLoginChange('text', e.target.value)} />
          </label>
          <label>Login - botón
            <input type="text" value={primaryForm.login?.button || ''} onChange={(e) => handlePrimaryLoginChange('button', e.target.value)} />
          </label>
        </div>
        <div className="row two-columns">
          <label>Formulario - abrir
            <input type="text" value={primaryForm.form?.showText || ''} onChange={(e) => handlePrimaryFormTextsChange('showText', e.target.value)} />
          </label>
          <label>Formulario - cerrar
            <input type="text" value={primaryForm.form?.hideText || ''} onChange={(e) => handlePrimaryFormTextsChange('hideText', e.target.value)} />
          </label>
        </div>
        <div className="row two-columns">
          <label>Calendario - título
            <input type="text" value={primaryForm.calendar?.title || ''} onChange={(e) => handlePrimaryCalendarChange('title', e.target.value)} />
          </label>
          <label>Calendario flotante
            <input type="text" value={primaryForm.calendar?.floatTitle || ''} onChange={(e) => handlePrimaryCalendarChange('floatTitle', e.target.value)} />
          </label>
        </div>
      </section>
      )}

      {activeTab === 'aux' && (
      <section className="card">
        <h3>Footer</h3>
        <label>Descripción breve
          <textarea
            value={primaryForm.footer?.description || ''}
            onChange={(e) =>
              setPrimaryForm((prev) => ({
                ...prev,
                footer: { ...(prev.footer || {}), description: e.target.value },
              }))
            }
          />
        </label>
      </section>
      )}

      {activeTab === 'aux' && (
      <section className="card">
        <h3>Contacto directo</h3>
        <label className="flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--admin-text)' }}>
          <input
            type="checkbox"
            checked={primaryForm.showContactSection !== false}
            onChange={(e) => handlePrimaryField('showContactSection', e.target.checked)}
          />
          Mostrar sección de contacto
        </label>
        <label>Título
          <input
            type="text"
            value={primaryForm.contactInfo?.title || ''}
            onChange={(e) => updateContactInfo('title', e.target.value)}
          />
        </label>
        <label>Descripción
          <textarea
            value={primaryForm.contactInfo?.description || ''}
            onChange={(e) => updateContactInfo('description', e.target.value)}
          />
        </label>
        <div className="row two-columns">
          <label>Email
            <input
              type="text"
              value={primaryForm.contactInfo?.email || ''}
              onChange={(e) => updateContactInfo('email', e.target.value)}
            />
          </label>
          <label>WhatsApp
            <input
              type="text"
              value={primaryForm.contactInfo?.whatsapp || ''}
              onChange={(e) => updateContactInfo('whatsapp', e.target.value)}
            />
          </label>
        </div>
        <div className="row two-columns">
          <label>Horario
            <input
              type="text"
              value={primaryForm.contactInfo?.hours || ''}
              onChange={(e) => updateContactInfo('hours', e.target.value)}
            />
          </label>
          <label>Regiones
            <input
              type="text"
              value={primaryForm.contactInfo?.location || ''}
              onChange={(e) => updateContactInfo('location', e.target.value)}
            />
          </label>
        </div>
        <label>Texto tarjeta CTA
          <textarea
            value={primaryForm.contactInfo?.ctaCard?.text || ''}
            onChange={(e) => updateContactCardInfo('text', e.target.value)}
          />
        </label>
        <label>Botón tarjeta CTA
          <input
            type="text"
            value={primaryForm.contactInfo?.ctaCard?.button || ''}
            onChange={(e) => updateContactCardInfo('button', e.target.value)}
          />
        </label>
      </section>
      )}

      {activeTab === 'aux' && (
      <section className="card">
        <h3>WhatsApp general</h3>
        <div className="row two-columns">
          <label>Número (sin +)
            <input type="text" value={primaryForm.whatsappPhone || ''} onChange={(e) => handlePrimaryWhatsappChange('whatsappPhone', e.target.value)} />
          </label>
          <label>Mensaje por defecto
            <input type="text" value={primaryForm.whatsappMessage || ''} onChange={(e) => handlePrimaryWhatsappChange('whatsappMessage', e.target.value)} />
          </label>
        </div>
        <label>Texto del botón modal
          <input type="text" value={primaryForm.whatsappModal?.buttonText || ''} onChange={(e) => handlePrimaryModalChange('buttonText', e.target.value)} />
        </label>
      </section>
      )}

      {primaryStatus && <p>{primaryStatus}</p>}

      <button type="button" className="save-btn" onClick={handlePrimarySave} disabled={primarySaving}>
        {primarySaving ? 'Guardando...' : 'Guardar landing principal'}
      </button>
    </>
  );

  return (
    <div className="landing-admin">
      <h2>Editor de Landing Puerto Rico Windows & Doors</h2>
      <p className="text-sm text-gray-500 mb-4">
        Ajusta cada sección del sitio principal desde este panel.
      </p>
      {uploadingHint && <p>{uploadingHint}</p>}
      {renderPrimaryEditor()}
    </div>
  );
};

export default LandingAdmin;
